import { useAccountsService } from '@restworld/data-services';
import { AxiosResponse } from 'axios';
import { useCallback, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import { useAuthContext } from '@restworld/feature-authentication';
import { useTranslation } from 'next-i18next';
import { useAnalytics } from '@restworld/utils-common';
import {
  ContentType as ModalContentType,
  closeAuthFlowModal,
  closeEmailTokenSentModal,
  setActiveContent as setActiveModalContent,
  updateEmailVerificationDetails,
} from '../../../context/auth-flow';

export type UseEmailOtpFormProps = {
  email: string;
  isModal?: boolean;
};

/**
 * Verifies the 6-digit code emailed alongside the magic link. Mirrors
 * useOTPForm (phone) but posts { email, otp_code } to /accounts/login and closes
 * the email-token-sent modal on success. Exposes `verify(code)` so the caller
 * can auto-submit from OTPInput's onChange once the code reaches 6 digits, no
 * button click required.
 */
export function useEmailOtpForm({ email, isModal = true }: UseEmailOtpFormProps) {
  const [loading, setLoading] = useState(false);
  const [serverValidationFailed, setServerValidationFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [isSuccess, setIsSuccess] = useState(false);
  // Guards against a double submit (auto-submit on complete + a button click).
  const inFlightRef = useRef(false);

  const acc = useAccountsService();
  const setActiveContent = useSetAtom(setActiveModalContent);
  const closeModal = useSetAtom(closeAuthFlowModal);
  const onCloseEmailTokenSentModal = useSetAtom(closeEmailTokenSentModal);
  const resetEmailDetails = useSetAtom(updateEmailVerificationDetails);
  const authContext = useAuthContext();
  const { t } = useTranslation('auth-flow');
  const { trackEvent } = useAnalytics();

  const closeTimeout = () => new Promise((resolve) => setTimeout(resolve, 1000));

  const verify = useCallback(
    async (otpCode: string) => {
      if (inFlightRef.current || otpCode.length !== 6) return;
      inFlightRef.current = true;
      setLoading(true);
      setServerValidationFailed(false);
      setErrorMessage(undefined);
      try {
        const response: AxiosResponse = await acc.verifyEmailOtp({ email, otpCode });
        const jwtToken = response?.data?.jwt || undefined;
        setIsSuccess(true);
        if (authContext?.verifyToken) {
          authContext.initializeAuthentication(jwtToken);
          trackEvent('complete_signin', {
            method: 'email_otp',
            email,
            user_id: authContext?.user?.id,
          });
        }
        await closeTimeout();
        // Clear the persisted send so a later sign-in starts fresh (the code was
        // just consumed) instead of resuming this now-dead code step.
        resetEmailDetails({
          email: '',
          waitTime: 0,
          tokenValidityTime: 0,
          tokenExpiresAt: undefined,
          waitExpiresAt: undefined,
        });
        setActiveContent(ModalContentType.NO_CONTENT);
        if (isModal) {
          onCloseEmailTokenSentModal();
          closeModal();
        }
      } catch (error) {
        setIsSuccess(false);
        setServerValidationFailed(true);
        setErrorMessage(t('email_login_otp_validation_failed'));
      } finally {
        setLoading(false);
        inFlightRef.current = false;
      }
    },
    [
      acc,
      email,
      authContext,
      trackEvent,
      setActiveContent,
      isModal,
      onCloseEmailTokenSentModal,
      closeModal,
      resetEmailDetails,
      t,
    ]
  );

  return { loading, isSuccess, serverValidationFailed, setServerValidationFailed, errorMessage, verify };
}
